import React, { useState, useEffect, useRef, useMemo } from "react";
import "./PhotosGallery.css";
import { useModal } from "../../contexts/ModalContext";
import ArtistInfo from "../Ilustrations/ArtistInfo";
import AudioPlayer from "../AudioPlayer/AudioPlayer";
import { useSearchParams } from "react-router-dom";
// Lista de fotos (uma por mídia de bird.photos, com ave e artista já resolvidos),
// montada uma única vez em data/photos.ts e compartilhada com a página do voador.
import { photos as ALL_PHOTOS, PhotoItem as Photo } from "../../data/photos";

// popularName vem em minúsculas no dicionário ("garça-branca-grande")
const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// Remove acentos, normaliza hífens/espaços e põe em minúsculas
const normalizeText = (str: string): string =>
  str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-\s]+/g, " ")
    .trim()
    .toLowerCase();

const PhotosGallery: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const { setIsModalOpen } = useModal();
  const modalTextRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Busca por nome comum, científico, nome em inglês ou artista
  const filteredPhotos = useMemo(() => {
    const term = normalizeText(searchTerm);
    if (!term) return ALL_PHOTOS;

    return ALL_PHOTOS.filter(({ bird, artist }) =>
      [
        bird.popularName,
        bird.scientificName,
        bird.englishName,
        artist?.name,
        artist?.fullName,
      ].some((field) => field && normalizeText(field).includes(term))
    );
  }, [searchTerm]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Abre o modal a partir de /fotos?bird=<id da ave em birds.ts>[&img=<posição>]
  // (usado pelas miniaturas da página do voador)
  useEffect(() => {
    const birdId = searchParams.get("bird");
    if (!birdId) return;

    const id = parseInt(birdId, 10);
    const index = parseInt(searchParams.get("img") ?? "0", 10);
    const photo =
      ALL_PHOTOS.find((p) => p.bird.id === id && p.index === index) ??
      ALL_PHOTOS.find((p) => p.bird.id === id);

    if (photo) {
      // Limpa a busca para a navegação anterior/próxima do modal funcionar
      setSearchTerm("");
      setSelectedPhoto(photo);
      // Remove o parâmetro depois de abrir o modal
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Initialize viewport height CSS custom property (igual ao Ilustrations.tsx)
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    setVH();
  }, []);

  // Prevent body scroll when modal is open and handle mobile viewport
  // (mesmo padrão do Ilustrations.tsx)
  useEffect(() => {
    if (selectedPhoto) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      if (window.innerHeight < window.outerHeight) {
        document.body.style.height = "100vh";
        document.body.style.height = "100dvh";
        document.documentElement.style.height = "100vh";
        document.documentElement.style.height = "100dvh";
      }

      let metaViewport = document.querySelector('meta[name="viewport"]');
      if (!metaViewport) {
        metaViewport = document.createElement("meta");
        metaViewport.setAttribute("name", "viewport");
        document.head.appendChild(metaViewport);
      }
      const originalContent = metaViewport.getAttribute("content");
      metaViewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, viewport-fit=cover, user-scalable=no"
      );

      setIsModalOpen(true);

      return () => {
        document.body.style.overflow = "unset";
        document.documentElement.style.overflow = "unset";
        document.body.style.height = "unset";
        document.documentElement.style.height = "unset";

        if (metaViewport && originalContent) {
          metaViewport.setAttribute("content", originalContent);
        }

        setIsModalOpen(false);
      };
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
      document.body.style.height = "unset";
      document.documentElement.style.height = "unset";
      setIsModalOpen(false);
    }

    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
      document.body.style.height = "unset";
      document.documentElement.style.height = "unset";
      setIsModalOpen(false);
    };
  }, [selectedPhoto, setIsModalOpen]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const preloadImage = (src: string) => {
    const img = new Image();
    img.src = src;
  };

  const preloadAdjacentImages = (currentPhoto: Photo) => {
    const currentIndex = filteredPhotos.findIndex(
      (photo) => photo.src === currentPhoto.src
    );
    const nextIndex = (currentIndex + 1) % filteredPhotos.length;
    const prevIndex =
      (currentIndex - 1 + filteredPhotos.length) % filteredPhotos.length;

    if (filteredPhotos[nextIndex]) preloadImage(filteredPhotos[nextIndex].src);
    if (filteredPhotos[prevIndex]) preloadImage(filteredPhotos[prevIndex].src);
  };

  const viewportHandler = useRef<(() => void) | null>(null);

  const openModal = (photo: Photo) => {
    setSelectedPhoto(photo);
    preloadAdjacentImages(photo);

    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    viewportHandler.current = setVH;
    setVH();
    window.addEventListener("resize", setVH);
    window.addEventListener("orientationchange", setVH);
  };

  const closeModal = () => {
    setSelectedPhoto(null);

    if (viewportHandler.current) {
      window.removeEventListener("resize", viewportHandler.current);
      window.removeEventListener("orientationchange", viewportHandler.current);
      viewportHandler.current = null;
    }
  };

  const nextPhoto = () => {
    if (!selectedPhoto) return;
    const currentIndex = filteredPhotos.findIndex(
      (photo) => photo.src === selectedPhoto.src
    );
    const nextIndex = (currentIndex + 1) % filteredPhotos.length;
    const newPhoto = filteredPhotos[nextIndex];
    setSelectedPhoto(newPhoto);
    preloadAdjacentImages(newPhoto);

    if (modalTextRef.current) {
      modalTextRef.current.scrollTop = 0;
    }
  };

  const prevPhoto = () => {
    if (!selectedPhoto) return;
    const currentIndex = filteredPhotos.findIndex(
      (photo) => photo.src === selectedPhoto.src
    );
    const prevIndex =
      (currentIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
    const newPhoto = filteredPhotos[prevIndex];
    setSelectedPhoto(newPhoto);
    preloadAdjacentImages(newPhoto);

    if (modalTextRef.current) {
      modalTextRef.current.scrollTop = 0;
    }
  };

  return (
    <div className="photos-gallery">
      <div className="gallery-container">
        <h1 className="gallery-title">Galeria de Fotos</h1>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Buscar por nome comum, científico ou artista..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            {searchTerm && (
              <button onClick={clearSearch} className="search-clear">
                ×
              </button>
            )}
          </div>
          <p className="search-results">
            {
              searchTerm
                ? `${filteredPhotos.length} foto${
                    filteredPhotos.length !== 1 ? "s" : ""
                  } encontrada${filteredPhotos.length !== 1 ? "s" : ""}`
                : "\u00A0"
            }
          </p>
        </div>

        {/* Grid em colunas desalinhadas — mesmo padrão do Ilustrations.tsx */}
        <div className="photos-grid">
          {Array.from({ length: 4 }).map((_, columnIndex) => (
            <div className="photos-column" key={columnIndex}>
              {filteredPhotos
                .filter((_, index) => index % 4 === columnIndex)
                .map((photo) => (
                  <div
                    key={photo.src}
                    className="photo-card"
                    onClick={() => openModal(photo)}
                  >
                    <div className="photo-image-container">
                      <img
                        src={photo.src}
                        alt={`${capitalize(photo.bird.popularName)} (${photo.bird.scientificName})`}
                        className="photo-image"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="photo-overlay">
                        <h3 className="photo-name">
                          {capitalize(photo.bird.popularName)}
                        </h3>
                        <p className="photo-scientific">
                          ({photo.bird.scientificName})
                        </p>
                        {photo.artist && (
                          <ArtistInfo
                            artist={photo.artist}
                            className="card-artist-info"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* Modal — mesmo padrão do modal de Ilustrations.tsx */}
      {selectedPhoto && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>

            <div className="modal-body">
              <div className="modal-image-container">
                <img
                  src={selectedPhoto.src}
                  alt={`${capitalize(selectedPhoto.bird.popularName)} (${selectedPhoto.bird.scientificName})`}
                  className="modal-image"
                />
              </div>

              <div className="modal-info">
                <div className="modal-text-content" ref={modalTextRef}>
                  <h2 className="modal-title">
                    {capitalize(selectedPhoto.bird.popularName)}
                  </h2>
                  <h3 className="modal-subtitle">
                    ({selectedPhoto.bird.scientificName})
                  </h3>

                  {selectedPhoto.artist && (
                    <ArtistInfo
                      artist={selectedPhoto.artist}
                      className="modal-artist-info"
                    />
                  )}

                  {selectedPhoto.bird.description && (
                    <p className="modal-description">
                      {selectedPhoto.bird.description}
                    </p>
                  )}
                </div>

                {selectedPhoto.bird.audio && (
                  <div className="modal-audio">
                    <span className="modal-audio-label">
                      <i className="fi fi-bs-volume"></i> Ouvir canto
                    </span>
                    <AudioPlayer src={selectedPhoto.bird.audio} />
                  </div>
                )}

                <div className="modal-navigation">
                  <button className="nav-button nav-prev" onClick={prevPhoto}>
                    <i className="fi fi-br-angle-left"></i>
                    Anterior
                  </button>
                  <button className="nav-button nav-next" onClick={nextPhoto}>
                    Próximo
                    <i className="fi fi-br-angle-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotosGallery;
